import RestoreIcon from "../../../../atom/icons/Restore";
import { useConfirmPopup } from "../../../../../hooks/popup/useOpenConfirmPopup";
import { ConfirmPopupResult, ConfirmPopupVariant } from "../../../../../types/popup";
import {
  useDeleteSpaceMutation,
  useDeleteSpacesMutation,
} from "../../../../../react-query/space/mutations";
import { useRestoreTrashedSpaceMutation } from "../../../../../react-query/space/deletion/useRestoreTrashedSpaceMutation";
import { juneTrack } from "../../../../../utils/june/analytics";
import { JUNE_EVENT, JUNE_LOCATION } from "../../../../../hooks/june/juneEvent";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { useSetRecoilState } from "recoil";
import { inProgressSpaceListState } from "../../../../../recoil/spaces/inProgressSpaceListState";
import { toastState } from "../../../../../recoil/toast/toastState";
import { getToastMessageByKind } from "../../../../../utils/toast/toastUtils";
import { ToastKind } from "../../../../../types/toast";
import { logger } from "../../../../../utils/logger";
import { ScrollArea } from "../../../../atom/ScrollArea";
import SpaceDot from "../../../../atom/Space/SpaceDot";
import { spaceQueryKeys, useGetSpaces } from "../../../../../react-query/space/queries";
import { SpaceStatus } from "../../../../../types/space/enum";
import TrashIcon from "../../../../atom/icons/TrashIcon";
import TrashListHoverButton from "./TrashListHoverButton";
import TrashEmptyButton from "./TrashEmptyButton";

const SpaceTabContent = () => {
  const setInProgressSpaceList = useSetRecoilState(inProgressSpaceListState);
  const setToast = useSetRecoilState(toastState);

  const queryClient = useQueryClient();
  const { data: spacesInTrash } = useGetSpaces({
    payload: {
      itemStatus: SpaceStatus.Deleted,
    },
  });
  const { mutate: deleteSpace } = useDeleteSpaceMutation({});
  const { mutate: restoreSpace } = useRestoreTrashedSpaceMutation();
  const { mutate: deleteAllSpaces } = useDeleteSpacesMutation({});

  const openConfirm = useConfirmPopup();

  /** Space 1개 복구 */
  function handleRestoreSpace(projectId: number) {
    const currentSpace = spacesInTrash?.find((space) => space.id === projectId) ?? null;

    setInProgressSpaceList((prev) => {
      if (!currentSpace) return prev;
      if (!prev) return [currentSpace];

      return [...prev, currentSpace];
    });

    restoreSpace(projectId, {
      onSuccess: () => {
        setToast({
          isVisible: true,
          type: ToastKind.RESTORE,
          message: getToastMessageByKind({
            type: ToastKind.RESTORE,
            target: "Space",
          }),
          triggerId: Date.now(),
        });
      },
      onError: (err) => {
        if (isAxiosError(err)) {
          setToast({
            isVisible: true,
            type: ToastKind.ERROR,
            message: getToastMessageByKind({
              type: ToastKind.ERROR,
              action: "restore",
            }),
            triggerId: Date.now(),
          });
        }
      },
    });
  }
  /** Space 1개 삭제 */
  async function handleDeleteSpace(spaceId: number) {
    const confirmResult = await openConfirm({
      headerTitle: "Permanently Delete",
      message: "Are you sure you want to permanently delete the item? You can't undo this action.",
      discardButtonTitle: "Cancel",
      doNotSendButtonTitle: "",
      okButtonTitle: "Delete",
      variant: ConfirmPopupVariant.DELETE,
    });

    // 사용자가 취소한 경우
    if (confirmResult === ConfirmPopupResult.CANCEL) return;

    deleteSpace(spaceId, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: spaceQueryKeys.status(SpaceStatus.Deleted),
        });
        juneTrack(JUNE_EVENT.DELETE_SPACE, {
          location: JUNE_LOCATION.TRASH,
        });
      },
    });
  }

  /** Spaces tab 전체 삭제 */
  function handleDeleteAllSpaces() {
    const spaceIds = spacesInTrash?.map((space) => space.id);

    if (spaceIds && spaceIds.length > 0) {
      deleteAllSpaces(spaceIds, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: spaceQueryKeys.status(SpaceStatus.Deleted),
          });
          juneTrack(JUNE_EVENT.CLICK_EMPTY, {
            type: "space",
          });
        },
        onError: (error: AxiosError) => {
          logger.error("Failed to process delete requests:", error);
        },
      });
    }
  }

  async function handleSpaceEmpty() {
    const confirmResult = await openConfirm({
      headerTitle: "Empty Trash",
      message: "Are you sure you want to permanently delete the items? You can't undo this action.",
      discardButtonTitle: "Cancel",
      doNotSendButtonTitle: "", // 숨김
      okButtonTitle: "Delete",
      variant: ConfirmPopupVariant.EMPTY,
    });

    // NOTE 사용자가 취소한 경우
    if (confirmResult === ConfirmPopupResult.CANCEL) return;

    handleDeleteAllSpaces();
  }

  if (!spacesInTrash || spacesInTrash.length === 0) return null;

  return (
    <div className="flex h-full flex-col">
      <ScrollArea
        height={430}
        showScrollBar={true}
        className="grow"
      >
        <ul className="flex flex-col gap-[8px] py-[4px] pl-[8px]">
          {spacesInTrash.map((space) => (
            <li
              key={space.id}
              className={
                "group relative flex h-9 w-full select-none items-center gap-1.5 rounded-[5px] px-2 text-[14px] leading-[150%] text-white hover:bg-w-8-hover data-[state=open]:bg-w-8-hover"
              }
            >
              <SpaceDot
                color={space.color}
                className="flex-shrink-0"
              />
              <div className="flex-grow overflow-hidden text-ellipsis whitespace-nowrap">
                {space.title}
              </div>
              <div className="absolute right-[4px] top-1/2 hidden -translate-y-1/2 gap-[4px] group-hover:flex">
                <TrashListHoverButton onClick={() => handleRestoreSpace(space.id)}>
                  <RestoreIcon color="#CACDCE" />
                </TrashListHoverButton>
                <TrashListHoverButton onClick={() => handleDeleteSpace(space.id)}>
                  <TrashIcon color="#CACDCE" />
                </TrashListHoverButton>
              </div>
            </li>
          ))}
        </ul>
      </ScrollArea>
      <div className="border-box flex h-[40px] flex-shrink-0 items-center justify-end border-t border-solid border-25 px-[12px]">
        <TrashEmptyButton onClick={handleSpaceEmpty}>Empty</TrashEmptyButton>
      </div>
    </div>
  );
};

export default SpaceTabContent;
